import React, { useEffect, useState, useRef } from "react";
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Modal } from 'bootstrap/dist/js/bootstrap';
import { CristalLoader } from "helpers/generic";
import { getCommunity } from "helpers/community";
import TrSkeleton from "components/TrSkeleton";
import CounterPartsSelector from 'components/CounterPartsSelector';

const ModalStyled = styled.div`
	background: rgba(0, 0, 0, 0.4);
`;

const CounterpartOldInfo = styled.div`
	font-size: 10px;
	color: var(--bs-gray-600);
	margin-bottom: 8px;

	b {
		font-weight: 500;
		color: var(--bs-dark);
	}

	ul {
		margin-bottom: 0;
		padding-left: 18px;
	}

	li {
		margin-bottom: 2px;
	}
`;

const CounterpartsTable = styled.table`
	margin-top: 8px;
	font-size: 11px;

	th {
		background: var(--bs-light);
		font-weight: 500;
		font-size: 11px;
		padding: 4px 8px;
	}

	td {
		padding: 2px 8px;
		vertical-align: middle;

		&:nth-child(1) {
			padding: 0;
		}

		&:nth-child(2) {
			position: relative;

			input {
				position: absolute;
				top: 0;
				left: 0;
				height: 100%;
				width: 100%;
				padding: 0 10px;
				border: 0;
				background: transparent;
				outline: none;
				text-align: center;
				font-size: 11px;
			}
		}

		small {
			font-size: 9px;
			margin-top: 2px;
		}
	}

	th, td {
		&:nth-child(1) {
			width: auto;
		}

		&:nth-child(2) {
			width: 70px;
			max-width: 70px;
			min-width: 70px;
		}

		&:nth-child(3) {
			width: 40px;
			max-width: 40px;
			min-width: 40px;
			text-align: center;
			vertical-align: middle;
			padding: 4px;

			button {
				background: transparent;
				border: 0;
				font-size: 13px;
				padding: 0;
				margin: 0;
				cursor: pointer;
				display: inline-flex;
				align-items: center;
				justify-content: center;

				i {
					line-height: 1;
				}
			}
		}
	}

	thead {
		th:nth-child(3) button {
			color: var(--bs-primary);

			&:hover {
				color: var(--bs-primary);
			}
		}
	}

	tbody {
		td:nth-child(3) button {
			color: var(--bs-danger);

			&:hover {
				color: var(--bs-danger);
			}
		}
	}
`;

let axiosCancelToken = null;

export default function RenewRulesModal(props) {
	const modalRef = useRef(null);

	const closeCallback = props.closeCallback;
	const estimate = props.estimate;

	const [rules, setRules] = useState(null);
	const [previousYear, setPreviousYear] = useState(null);
	const [selectedRules, setSelectedRules] = useState({});
	const [newCounterparts, setNewCounterparts] = useState({});
	const [method, setMethod] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		const modal = new Modal(modalRef.current, {
			backdrop: false
		});

		const hiddenEvent = () => {
           	modal._element.removeEventListener('hidden.bs.modal', hiddenEvent);
           	modal._element.removeEventListener('shown.bs.modal', hiddenEvent);
			modal.dispose();
			closeCallback();

			// Fix, because bootstrap removes scroll and add padding on modal opened
			document.body.style.overflow = 'auto';
			document.body.style.paddingRight = '0';
		}

		modal._element.addEventListener('hidden.bs.modal', hiddenEvent);

		modal.show();

		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, [closeCallback]);

    useEffect(() => {
		const estimateYear = moment(estimate.date).year();

		const getPreviousYear = async () => {
			setLoading(true);

			await axios.get('/api/manager/bank-reconciliation-rules/get-previous-available-year', {
				params: {
					year: estimateYear,
					community_id: getCommunity()?.id
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
		    	setPreviousYear(response.data.previous_year);
		  	}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			});

			setLoading(false);
		}

		getPreviousYear();
    }, [estimate]);

	useEffect(() => {
		if (previousYear === null) return;

        const getRules = async () => {
			setLoading(true);

			const estimateYear = moment(estimate.date).year();

			await axios.get('/api/manager/bank-reconciliation-rules/list', {
				params: {
					community_id: getCommunity()?.id,
					year: previousYear,
					no_paginate: true,
					exclude_renewed_for_year: estimateYear
				},
				cancelToken: axiosCancelToken.token
			}).then((response) => {
		    	setRules([...response.data]);

				// Para cada regla de tipo counterpart, obtener matches
				response.data.forEach((rule) => {
					if (rule.type === 'counterpart') {
						matchCounterpartsForRule(rule);
					}
				});
		  	}).catch((error) => {
				if ( axios.isCancel(error) ) return;
			});

			setLoading(false);
		}

		getRules();
    }, [previousYear, estimate]);

	const matchCounterpartsForRule = async (rule) => {
		await axios.post('/api/manager/bank-reconciliation-rules/match-counterparts', {
			rule_id: rule.id,
			estimate_id: estimate.id,
			community_id: getCommunity()?.id
		}, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			// Inicializar contrapartidas nuevas con los matches encontrados
			const counterpartsArray = response.data.matches.map((match) => ({
				id: match.new_counterpart_id,
				counterpart: match.new_counterpart,
				percent: match.percent,
				old_counterpart_id: match.old_counterpart_id,
				matched: match.matched
			}));

			setNewCounterparts(prev => ({
				...prev,
				[rule.id]: counterpartsArray
			}));
		}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});
	}

    const toggleSelectedRule = (ruleId) => {
        let newSelectedRules = {...selectedRules};

        if ( newSelectedRules[ruleId] ) {
			delete newSelectedRules[ruleId];
		} else {
			newSelectedRules[ruleId] = true;
		}

        setSelectedRules(newSelectedRules);
    }

	const toggleSelectAll = () => {
		let newSelectedRules = {};

		if ( Object.keys(selectedRules).length !== rules.length ) {
			rules?.forEach((rule) => {
				newSelectedRules[rule.id] = true;
			});
		}

		setSelectedRules(newSelectedRules);
	}

	const addCounterpart = (ruleId) => {
		const currentCounterparts = newCounterparts[ruleId] || [];
		const newCounterpart = {
			id: null,
			counterpart: null,
			percent: currentCounterparts.length === 0 ? 100 : null,
			old_counterpart_id: null,
			matched: false
		};

		setNewCounterparts(prev => ({
			...prev,
			[ruleId]: [...currentCounterparts, newCounterpart]
		}));
	}

	const removeCounterpart = (ruleId, index) => {
		const currentCounterparts = newCounterparts[ruleId] || [];
		const updatedCounterparts = currentCounterparts.filter((_, i) => i !== index);

		setNewCounterparts(prev => ({
			...prev,
			[ruleId]: updatedCounterparts
		}));
	}

	const updateCounterpart = (ruleId, index, field, value) => {
		const currentCounterparts = [...(newCounterparts[ruleId] || [])];
		currentCounterparts[index] = {
			...currentCounterparts[index],
			[field]: value
		};

		if (field === 'counterpart') {
			currentCounterparts[index].id = value?.id;
		}

		setNewCounterparts(prev => ({
			...prev,
			[ruleId]: currentCounterparts
		}));
	}

	const handleRenew = async () => {
		// Validar que todas las reglas de tipo counterpart tengan contrapartidas seleccionadas
		const selectedRuleIds = Object.keys(selectedRules);

		for (let i = 0; i < selectedRuleIds.length; i++) {
			const ruleId = selectedRuleIds[i];
			const rule = rules.find(r => r.id == ruleId);

			if (rule && rule.type === 'counterpart') {
				const counterparts = newCounterparts[ruleId] || [];

				// Verificar que tenga al menos una contrapartida
				if (counterparts.length === 0) {
					toast.error('La regla de contrapartidas debe tener al menos una contrapartida seleccionada');
					return;
				}

				// Verificar que todas las contrapartidas tengan un id seleccionado
				const hasEmptyCounterpart = counterparts.some(cp => !cp.id);
				if (hasEmptyCounterpart) {
					toast.error('Todas las contrapartidas deben estar seleccionadas');
					return;
				}

				// Verificar que todas tengan un porcentaje válido
				const hasInvalidPercent = counterparts.some(cp => !cp.percent || cp.percent <= 0);
				if (hasInvalidPercent) {
					toast.error('Todas las contrapartidas deben tener un porcentaje mayor a 0');
					return;
				}

				// Verificar que la suma de porcentajes sea 100
				const totalPercent = counterparts.reduce((sum, cp) => sum + parseFloat(cp.percent || 0), 0);
				if (Math.abs(totalPercent - 100) > 0.01) {
					toast.error('La suma de los porcentajes debe ser 100');
					return;
				}
			}
		}

		const selectedRulesData = selectedRuleIds.map(ruleId => {
			return {
				rule_id: parseInt(ruleId),
				new_counterparts: (newCounterparts[ruleId] || []).map(cp => ({
					id: cp.id,
					percent: parseFloat(cp.percent)
				}))
			};
		});

		setLoading(true);

		await axios.post('/api/manager/bank-reconciliation-rules/renew', {
			estimate_id: estimate.id,
			method: method,
			rules: selectedRulesData,
			community_id: getCommunity()?.id
		}, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			toast.success(response.data.message || 'Reglas renovadas correctamente');
			Modal.getInstance(modalRef.current).hide();
		}).catch((error) => {
			if (axios.isCancel(error)) return;

			if (error.response?.data?.errors) {
				// Mostrar el primer error de validación
				const firstError = Object.values(error.response.data.errors)[0];
				toast.error(Array.isArray(firstError) ? firstError[0] : firstError);
			} else if (error.response?.data?.message) {
				toast.error(error.response.data.message);
			} else {
				toast.error('Ha ocurrido un error al renovar las reglas');
			}
		});

		setLoading(false);
	}

	return (
		<ModalStyled className="modal" tabIndex="-1" ref={modalRef}>
			<div className="modal-dialog modal-xl">
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">Renovar Reglas</h5>
						<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div className="modal-body">
						<h5 className="mb-2 sbold">Presupuesto {estimate?.number_full}</h5>
						{previousYear === null && !loading && <p className="text-muted small mb-3">No hay reglas de años anteriores disponibles</p>}

						{previousYear !== null && (
							<table className="table table-sm table-bordered mb-0">
								<thead>
									<tr>
										<th className="sbold" style={{width: '20px'}}>
											<input
												type="checkbox"
												checked={rules?.length > 0 && Object.keys(selectedRules).length === rules.length}
												onChange={toggleSelectAll}
											/>
										</th>
										<th className="sbold">Nombre</th>
										<th className="sbold" style={{width: '10px'}}>Año</th>
										<th className="sbold">Detalles</th>
										<th className="sbold" style={{width: '10px'}}>Estado</th>
									</tr>
								</thead>
								<tbody>
									{ !loading && rules?.length > 0 &&
										rules.map((el, idx) => {
											return (
												<tr key={idx}>
													<td>
														<input
															type="checkbox"
															checked={selectedRules[el.id] || false}
															onChange={() => toggleSelectedRule(el.id)}
														/>
													</td>
													<td>
														{el.type === 'counterpart' && 'Contrapartidas'}
														{el.type === 'bankremittance' && 'Remesa bancaria'}
														{el.renew_from_id &&
															<span className="badge bg-danger ms-2">RENOVADA</span>
														}
													</td>
													<td>{el.year}</td>
													<td>
														{el.type === 'counterpart' &&
															<>
																<CounterpartOldInfo>
																	<div className="mb-1"><b>Término de búsqueda:</b> {el.data?.term}</div>
																	<div>
																		<b>Contrapartidas anteriores:</b>
																		<ul>
																			{el.data?.counterparts?.map((item, idx) => {
																				return (
																					<li key={idx}>
																						{item.counterpart?.name_full_path} ({item.percent}%)
																					</li>
																				);
																			})}
																		</ul>
																	</div>
																</CounterpartOldInfo>

																<div className="mb-1" style={{fontSize: '10px'}}><b>Contrapartidas nuevas:</b></div>
																<CounterpartsTable className="table table-sm table-bordered mb-0">
																	<thead>
																		<tr>
																			<th>Contrapartida</th>
																			<th>%</th>
																			<th>
																				<button
																					onClick={() => addCounterpart(el.id)}
																					type="button"
																				>
																					<i className="bi bi-plus-circle-fill text-primary"></i>
																				</button>
																			</th>
																		</tr>
																	</thead>
																	<tbody>
																		{(!newCounterparts[el.id] || newCounterparts[el.id]?.length === 0) && (
																			<tr>
																				<td colSpan="3" className="text-center" style={{fontSize: '10px', padding: '8px'}}>
																					No hay contrapartidas
																				</td>
																			</tr>
																		)}
																		{newCounterparts[el.id]?.map((counterpart, cpIdx) => (
																			<tr key={cpIdx}>
																				<td>
																					<CounterPartsSelector
																						onChange={value => updateCounterpart(el.id, cpIdx, 'counterpart', value)}
																						year={moment(estimate.date).year()}
																						canAdd={false}
																						value={counterpart.counterpart}
																					/>
																					{!counterpart.matched && counterpart.old_counterpart_id && !counterpart.id && (
																						<small className="text-danger d-block ms-2 mb-2">⚠ No se encontraron contrapartidas que coincidan</small>
																					)}
																				</td>
																				<td>
																					<input
																						type="number"
																						value={counterpart.percent ?? ''}
																						onChange={(e) => updateCounterpart(el.id, cpIdx, 'percent', e.target.value)}
																						className="no-arrows"
																					/>
																				</td>
																				<td>
																					<button
																						type="button"
																						onClick={() => removeCounterpart(el.id, cpIdx)}
																					>
																						<i className="bi bi-x-circle-fill"></i>
																					</button>
																				</td>
																			</tr>
																		))}
																	</tbody>
																</CounterpartsTable>
															</>
														}
														{el.type === 'bankremittance' &&
															<span><b>Término de búsqueda:</b> {el.data?.term}</span>
														}
													</td>
													<td>
														<span className={`badge ${el.active ? 'bg-success' : 'bg-secondary'}`}>
															{el.active ? 'Activa' : 'Inactiva'}
														</span>
													</td>
												</tr>
											)
										})
									}
									{rules !== null && rules?.length === 0 && <tr><td colSpan="5">No hay reglas disponibles para el año {previousYear}</td></tr>}
									{loading && <TrSkeleton rows={5} columns={5} /> }
								</tbody>
							</table>
						)}
					</div>
					<div className="modal-footer">
						<select
							className="form-select form-select-sm d-inline-block me-2"
							style={{width: '200px'}}
							value={method}
							onChange={(e) => setMethod(e.target.value)}
							disabled={Object.keys(selectedRules).length === 0}
						>
							<option value="">- Seleccionar método -</option>
							<option value="sustitucion">Sustitución</option>
							<option value="copia">Copia</option>
						</select>
						<button
							type="button"
							className="btn btn-sm btn-primary text-white"
							onClick={handleRenew}
							disabled={!method || Object.keys(selectedRules).length === 0}
						>
							Renovar
						</button>
					</div>
				</div>
			</div>
			{ loading && <CristalLoader /> }
		</ModalStyled>
	);
}
